import { PatientData } from './patient-data';
import { UserData } from './user-data';

function getComponentsForHide(allFields: string[], allowedFields: string[]) {
  return allFields.map((key) => ({
    key,
    ignore: !allowedFields.includes(key),
  }));
}

// List of all possible Display fields
const allDisplayFields = [
  'label',
  'placeholder',
  'description',
  'tooltip',
  'labelPosition',
  'widget.type',
  'inputMask',
  'displayMask',
  'allowMultipleMasks',
  'customClass',
  'prefix',
  'suffix',
  'hidden',
  'hideLabel',
  'showWordCount',
  'showCharCount',
  'autofocus',
  'spellcheck',
  'disabled',
  'tableView',
  'modalEdit',
  'hideInput',
  'mask',
  'tabindex',
  'autocomplete',
  'applyMaskOn',
  'isUploadEnabled',
  'labelWidth',
  'labelMargin',
  'shortcut',
  'refreshOnChange',
  // Signature
  'footer',
  'backgroundColor',
  'columns',
  'autoAdjust',
  // Table
  'cloneRows',
];

const allowedTextDisplayFields = ['label', 'placeholder', 'description', 'tooltip', 'hideLabel'];
const allowedContentDisplayFields = ['label'];
const allowedCheckboxDisplayFields = ['label', 'description', 'tooltip', 'hideLabel'];

const allValidationFields = [
  'validate.required',
  'validate.custom',
  'validate.customMessage',
  'validate.json',
  'validate.unique',
  'validate.multiple',
  'validate.uniqueMessage',
  'validate.minLength',
  'validate.maxLength',
  'validate.minWords',
  'validate.maxWords',
  'validate.minValue',
  'validate.maxValue',
  'validate.step',
  'validate.pattern',
  'validate.onlyAvailableItems',
  'validate.minSelectedCount',
  'validate.maxSelectedCount',
  'minSelectedCountMessage',
  'maxSelectedCountMessage',
  'validateOn',
  'unique',
  'validateWhenHidden',
  'errorLabel',
  'custom-validation-js',
  'errors',
  'json-validation-json',
  'persistent',
];

function getValidationComponentsForHide(allowedFields: string[]) {
  return allValidationFields.map((key) => ({
    key,
    ignore: !allowedFields.includes(key),
  }));
}

export const builderOptions = {
  builder: {
    basic: false,
    advanced: false,
    pdf: false,
    premium: false,
    data: false,
    layout: {
      weight: 0,
      default: true,
      components: {
        columns: false,
        content: false,
        fieldset: false,
        htmlelement: false,
        panel: false,
        tabs: false,
        well: false,
        columnsTwo: {
          title: '2 Column',
          icon: 'columns',
          weight: 10,
          schema: {
            label: '2 Column',
            columns: [
              {
                components: [],
                offset: 0,
                push: 0,
                pull: 0,
                size: 'md',
                currentWidth: 6,
                width: 6,
              },
              {
                components: [],
                offset: 0,
                push: 0,
                pull: 0,
                size: 'md',
                currentWidth: 6,
                width: 6,
              },
            ],
            key: 'columnsTwo',
            type: 'columns',
            input: false,
            tableView: false,
          },
        },
        columnsThree: {
          title: '3 Column',
          icon: 'columns',
          weight: 10,
          schema: {
            label: '3 Column',
            columns: [
              {
                components: [],
                offset: 0,
                push: 0,
                pull: 0,
                size: 'md',
                currentWidth: 4,
                width: 4,
              },
              {
                components: [],
                offset: 0,
                push: 0,
                pull: 0,
                size: 'md',
                currentWidth: 4,
                width: 4,
              },
              {
                components: [],
                size: 'md',
                currentWidth: 4,
                width: 4,
              },
            ],
            key: 'columnsThree',
            type: 'columns',
            input: false,
            tableView: false,
          },
        },
        columnsFour: {
          title: '4 Column',
          icon: 'columns',
          weight: 10,
          schema: {
            label: '4 Column',
            columns: [
              {
                components: [],
                offset: 0,
                push: 0,
                pull: 0,
                size: 'md',
                currentWidth: 3,
                width: 3,
              },
              {
                components: [],
                offset: 0,
                push: 0,
                pull: 0,
                size: 'md',
                currentWidth: 3,
                width: 3,
              },
              {
                components: [],
                size: 'md',
                width: 3,
                currentWidth: 3,
              },
              {
                components: [],
                size: 'md',
                width: 3,
                currentWidth: 3,
              },
            ],
            key: 'columnsFour',
            type: 'columns',
            input: false,
            tableView: false,
          },
        },
      },
    },
    customBasic: {
      title: 'Form Fields',
      default: false,
      weight: 10,
      components: {
        email: false,
        phoneNumber: false,
        content: {
          title: 'Content',
          icon: 'fa fa-edit',
          schema: {
            type: 'content',
            input: 'false',
            label: 'Content',
            key: 'content',
            html: '',
          },
        },
        textrow: {
          title: 'Text Row',
          icon: 'fa fa-terminal',
          schema: {
            type: 'textfield',
            input: true,
            label: 'Text Row',
          },
        },
        textarea: true,
        checkbox: true,
        selectboxes: true,
        select: {
          title: 'Dropdown',
          icon: 'fa fa-th-list',
          schema: {
            type: 'selectboxes',
            input: true,
            label: 'Dropdown',
          },
        },
        radio: {
          title: 'Radio',
          icon: 'dot-circle',
          schema: {
            type: 'radio',
            inputType: 'radio',
            label: 'Radio',
            key: 'radio',
            values: [{ label: '', value: '' }],
            data: {
              url: '',
            },
            fieldSet: false,
          },
        },
        datetime: {
          title: 'Date / Time',
          icon: 'calendar',
          schema: {
            label: 'Date / Time',
            type: 'datetime',
            key: 'dateTime',
            disabled: true,
          },
        },
        signature: {
          title: 'Signature',
          key: 'signature',
          icon: 'fa fa-pencil',
          schema: {
            label: 'Signature',
            type: 'signature',
            key: 'signature',
            input: true,
            backgroundColor: 'transparent',
            width: '-webkit-fill-available',
            height: 'auto',
            footer: 'Sign above',
          },
        },
        //   fieldset: false,
        //   panel: false,
        //   table: false,
        //   tabs: false,
        //   well: false,
        //   content: {
        //     title: 'Content',
        //     icon: 'far fa-edit',
        //     weight: 10,
        //     schema: {
        //       type: 'content',
        //       input: 'false',
        //       label: 'Content',
        //       key: 'content',
        //       html: '',
        //     },
        //   },
        // },
        htmlelement: {
          title: 'HTML Code',
          icon: 'code',
          weight: 10,
          schema: {
            label: 'HTML Code',
            key: 'htmlelement',
            type: 'htmlelement',
          },
        },
        sketchpad: true,
      },
    },
    patientData: PatientData,
    userData: UserData,
  },
  editForm: {
    textfield: [
      {
        key: 'display',
        components: getComponentsForHide(allDisplayFields, allowedTextDisplayFields),
      },
      {
        key: 'validation',
        components: getValidationComponentsForHide(['validate.required']),
      },
      {
        key: 'data',
        ignore: true,
      },
      {
        key: 'api',
        ignore: true,
      },
      {
        key: 'conditional',
        ignore: true,
      },
      {
        key: 'logic',
        ignore: true,
      },
      {
        key: 'layout',
        ignore: true,
      },
    ],
    textarea: [
      {
        key: 'display',
        components: getComponentsForHide(allDisplayFields, allowedTextDisplayFields),
      },
      {
        key: 'validation',
        components: getValidationComponentsForHide(['validate.required']),
      },
      {
        key: 'data',
        ignore: true,
      },
      {
        key: 'api',
        ignore: true,
      },
      {
        key: 'conditional',
        ignore: true,
      },
      {
        key: 'logic',
        ignore: true,
      },
      {
        key: 'layout',
        ignore: true,
      },
    ],
    content: [
      {
        key: 'display',
        components: getComponentsForHide(allDisplayFields, allowedContentDisplayFields),
      },
      {
        key: 'api',
        ignore: true,
      },
      {
        key: 'conditional',
        ignore: true,
      },
      {
        key: 'logic',
        ignore: true,
      },
      {
        key: 'layout',
        ignore: true,
      },
    ],
    htmlelement: [
      {
        key: 'display',
        components: [
          { key: 'className', ignore: true },
          { key: 'attrs', ignore: true },
          { key: 'refreshOnChange', ignore: true },
          { key: 'customClass', ignore: true },
          { key: 'placeholder', ignore: true },
          { key: 'prefix', ignore: true },
          { key: 'suffix', ignore: true },
          { key: 'tabindex', ignore: true },
          { key: 'hidden', ignore: true },
          { key: 'hideLabel', ignore: true },
          { key: 'disabled', ignore: true },
          { key: 'tableView', ignore: true },
          { key: 'modalEdit', ignore: true },
          { key: 'autofocus', ignore: true },
          { key: 'spellcheck', ignore: true },
          { key: 'widget.type', ignore: true },
          { key: 'labelPosition', ignore: true },
          { key: 'inputMask', ignore: true },
          { key: 'displayMask', ignore: true },
          { key: 'allowMultipleMasks', ignore: true },
          { key: 'showWordCount', ignore: true },
          { key: 'showCharCount', ignore: true },
          { key: 'mask', ignore: true },
          { key: 'autocomplete', ignore: true }, // optional description (rarely used)
        ],
      },
      {
        key: 'data',
        ignore: true,
      },
      {
        key: 'api',
        ignore: true,
      },
      {
        key: 'conditional',
        ignore: true,
      },
      {
        key: 'logic',
        ignore: true,
      },
      {
        key: 'validation',
        ignore: true,
      },
      {
        key: 'layout',
        ignore: true,
      },
    ],
    columns: [
      {
        key: 'display',
        components: getComponentsForHide(allDisplayFields, allowedContentDisplayFields),
      },
      {
        key: 'api',
        ignore: true,
      },
      {
        key: 'conditional',
        ignore: true,
      },
      {
        key: 'logic',
        ignore: true,
      },
      {
        key: 'layout',
        ignore: true,
      },
    ],
    datetime: [
      {
        key: 'display',
        components: [
          { key: 'label' },
          { key: 'placeholder' },
          { key: 'description' },
          { key: 'tooltip' },

          // explicitly hide unnecessary fields
          { key: 'customClass', ignore: true },
          { key: 'hidden', ignore: true },
          { key: 'hideLabel', ignore: true },
          { key: 'disabled', ignore: true },
          { key: 'tableView', ignore: true },
          { key: 'modalEdit', ignore: true },
          { key: 'tabindex', ignore: true },
          { key: 'autofocus', ignore: true },
          { key: 'spellcheck', ignore: true },
          { key: 'widget.type', ignore: true },
          { key: 'labelPosition', ignore: true },
          { key: 'prefix', ignore: true },
          { key: 'suffix', ignore: true },
          { key: 'showWordCount', ignore: true },
          { key: 'showCharCount', ignore: true },
          { key: 'inputMask', ignore: true },
          { key: 'displayMask', ignore: true },
          { key: 'allowMultipleMasks', ignore: true },
          { key: 'autocomplete', ignore: true },
          { key: 'displayInTimezone', ignore: true },
          { key: 'displayInTimezone', ignore: true },
          { key: 'useLocaleSettings', ignore: true },
          { key: 'format', ignore: true },
          { key: 'shortcutButtons', ignore: true },
          { key: 'allowInput', ignore: true },
        ],
      },
      {
        key: 'date',
        ignore: true,
      },
      {
        key: 'time',
        components: [
          { key: 'timePicker.hourStep', ignore: true },
          { key: 'timePicker.minuteStep', ignore: true },
          { key: 'timePicker.showMeridian', ignore: true },
        ],
      },
      {
        key: 'data',
        ignore: true,
      },
      {
        key: 'api',
        ignore: true,
      },
      {
        key: 'conditional',
        ignore: true,
      },
      {
        key: 'logic',
        ignore: true,
      },
      {
        key: 'validation',
        ignore: true,
      },
      {
        key: 'layout',
        ignore: true,
      },
    ],
    checkbox: [
      {
        key: 'display',
        components: getComponentsForHide(allDisplayFields, allowedCheckboxDisplayFields),
      },
      {
        key: 'validation',
        components: getValidationComponentsForHide(['validate.required']),
      },
      {
        key: 'data',
        ignore: true,
      },
      {
        key: 'api',
        ignore: true,
      },
      {
        key: 'conditional',
        ignore: true,
      },
      {
        key: 'logic',
        ignore: true,
      },
      {
        key: 'layout',
        ignore: true,
      },
    ],
    selectboxes: [
      {
        key: 'display',
        components: getComponentsForHide(allDisplayFields, allowedCheckboxDisplayFields),
      },
      {
        key: 'validation',
        components: getValidationComponentsForHide(['validate.required']),
      },
      {
        key: 'data',
        components: [
          { key: 'values', ignore: false }, // keep only Values array editor
          // Explicitly hide all others
          { key: 'dataSrc', ignore: true },
          { key: 'data.json', ignore: true },
          { key: 'data.url', ignore: true },
          { key: 'data.resource', ignore: true },
          { key: 'data.custom', ignore: true },
          { key: 'valueProperty', ignore: true },
          { key: 'template', ignore: true },
          { key: 'refreshOn', ignore: true },
          { key: 'clearOnRefresh', ignore: true },
          { key: 'searchEnabled', ignore: true },
          { key: 'selectThreshold', ignore: true },
          { key: 'lazyLoad', ignore: true },
          { key: 'limit', ignore: true },
          { key: 'uniqueOptions', ignore: true },
          { key: 'idPath', ignore: true },
          { key: 'protected', ignore: true },
          { key: 'persistent', ignore: true },
          { key: 'dbIndex', ignore: true },
          { key: 'encrypted', ignore: true },
          { key: 'defaultValue', ignore: true },
          { key: 'multiple', ignore: true },
          { key: 'clearOnHide', ignore: true },
          { key: 'customDefaultValuePanel', ignore: true },
          { key: 'calculateValuePanel', ignore: true },
          { key: 'calculateServer', ignore: true },
          { key: 'allowCalculateOverride', ignore: true },
        ],
      },
      {
        key: 'api',
        ignore: true,
      },
      {
        key: 'conditional',
        ignore: true,
      },
      {
        key: 'logic',
        ignore: true,
      },
      {
        key: 'layout',
        ignore: true,
      },
    ],
    select: [
      {
        key: 'display',
        components: getComponentsForHide(allDisplayFields, allowedCheckboxDisplayFields),
      },
      {
        key: 'validation',
        components: getValidationComponentsForHide(['validate.required']),
      },
      {
        key: 'data',
        ignore: false,
      },
      {
        key: 'api',
        ignore: true,
      },
      {
        key: 'conditional',
        ignore: true,
      },
      {
        key: 'logic',
        ignore: true,
      },
      {
        key: 'layout',
        ignore: true,
      },
    ],
    radio: [
      {
        key: 'display',
        components: getComponentsForHide(allDisplayFields, allowedCheckboxDisplayFields),
      },
      {
        key: 'validation',
        components: getValidationComponentsForHide(['validate.required']),
      },
      {
        key: 'data',
        components: [
          { key: 'values', ignore: false }, // keep only Values array editor
          // Explicitly hide all others
          { key: 'dataSrc', ignore: true },
          { key: 'data.json', ignore: true },
          { key: 'data.url', ignore: true },
          { key: 'data.resource', ignore: true },
          { key: 'data.custom', ignore: true },
          { key: 'valueProperty', ignore: true },
          { key: 'template', ignore: true },
          { key: 'refreshOn', ignore: true },
          { key: 'clearOnRefresh', ignore: true },
          { key: 'searchEnabled', ignore: true },
          { key: 'selectThreshold', ignore: true },
          { key: 'lazyLoad', ignore: true },
          { key: 'limit', ignore: true },
          { key: 'uniqueOptions', ignore: true },
          { key: 'idPath', ignore: true },
          { key: 'protected', ignore: true },
          { key: 'persistent', ignore: true },
          { key: 'dbIndex', ignore: true },
          { key: 'encrypted', ignore: true },
          { key: 'defaultValue', ignore: true },
          { key: 'multiple', ignore: true },
          { key: 'clearOnHide', ignore: true },
          { key: 'customDefaultValuePanel', ignore: true },
          { key: 'calculateValuePanel', ignore: true },
          { key: 'calculateServer', ignore: true },
          { key: 'allowCalculateOverride', ignore: true },
          { key: 'dataType', ignore: true },
        ],
      },
      {
        key: 'api',
        ignore: true,
      },
      {
        key: 'conditional',
        ignore: true,
      },
      {
        key: 'logic',
        ignore: true,
      },
      {
        key: 'layout',
        ignore: true,
      },
    ],
    signature: [
      {
        key: 'display',
        components: getComponentsForHide(allDisplayFields, allowedCheckboxDisplayFields),
      },
      {
        key: 'data',
        ignore: true,
      },
      {
        key: 'api',
        ignore: true,
      },
      {
        key: 'conditional',
        ignore: true,
      },
      {
        key: 'logic',
        ignore: true,
      },
      {
        key: 'validation',
        components: getValidationComponentsForHide(['validate.required']),
      },
      {
        key: 'layout',
        ignore: true,
      },
    ],
    table: [
      {
        key: 'display',
        components: getComponentsForHide(allDisplayFields, allowedContentDisplayFields),
      },
      {
        key: 'api',
        ignore: true,
      },
      {
        key: 'conditional',
        ignore: true,
      },
      {
        key: 'logic',
        ignore: true,
      },
      {
        key: 'layout',
        ignore: true,
      },
    ],
    sketchpad: [
      {
        key: 'display',
        components: getComponentsForHide(allDisplayFields, allowedCheckboxDisplayFields),
      },
      {
        key: 'validation',
        ignore: true,
      },
      {
        key: 'data',
        ignore: true,
      },
      {
        key: 'api',
        ignore: true,
      },
      {
        key: 'conditional',
        ignore: true,
      },
      {
        key: 'logic',
        ignore: true,
      },
      {
        key: 'layout',
        ignore: true,
      },
    ],
  },
};
